import styled from "@emotion/styled/macro";
import {
  Pill,
  useSingletonMobileMediaQuery,
} from "@sunrun/experience-ui-components";
import {
  Typography,
  SvgIcon,
  SvgNames,
  tokens,
} from "@sunrun/experience-ui-components";
import React from "react";
import { type ReactNode, isValidElement } from "react";
import { PersistentTooltip } from "checkout/components/atoms/PersistentTooltip";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { getCustomerProfileUrl } from "checkout/util/constants";

type TaskHeaderProps = {
  showAdditionalSystemPill?: boolean;
  icon: string | ReactNode;
  iconSize?: number;
  label: string;
};

const TaskHeader = ({
  icon,
  label,
  iconSize = 24,
  showAdditionalSystemPill = false,
}: TaskHeaderProps) => {
  const isMobile = useSingletonMobileMediaQuery();
  const { hasAdditionalSystem, prospectId } = useCheckoutContext();

  const TaskIcon = React.memo(() => {
    if (isValidElement(icon)) {
      return icon;
    }

    if (typeof icon === "string") {
      if (Object.values(SvgNames).includes(icon as SvgNames)) {
        return <SvgIcon width={iconSize} name={icon as SvgNames} />;
      }

      return <Icon src={icon} $size={iconSize} />;
    }

    return null;
  });

  return (
    <Header isMobile={isMobile}>
      <TitleContainer>
        <TaskIcon />
        <Typography size={tokens.FONT_SIZE_3} color={tokens.BRAND_HEROBLUE}>
          {label}
        </Typography>
      </TitleContainer>
      {showAdditionalSystemPill && (
        <PersistentTooltip
          isFullWidth={isMobile}
          direction={isMobile ? "bottom-left" : "bottom-right"}
          trigger={
            <Pill
              type="info"
              text={
                hasAdditionalSystem
                  ? "Solar panels installed"
                  : "No solar panels installed"
              }
            />
          }
        >
          <Typography size={tokens.FONT_SIZE_1} color={tokens.BRAND_HEROBLUE}>
            If you need to edit this, please go to{" "}
            <ProfileLink
              href={getCustomerProfileUrl(prospectId, "property")}
              target="_blank"
              rel="noopener noreferrer"
            >
              customer profile
            </ProfileLink>
          </Typography>
        </PersistentTooltip>
      )}
    </Header>
  );
};

const Icon = styled.img<{ $size: number }>`
  height: ${(props) => props.$size}px;
  width: ${(props) => props.$size}px;
`;

const Header = styled.div<{ isMobile?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: space-between;

  ${({ isMobile }) =>
    isMobile &&
    `
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  `}
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ProfileLink = styled.a`
  color: ${tokens.BRAND_HEROBLUE};
  text-decoration: underline;
  font-weight: 600;

  &:hover,
  &:focus-visible {
    color: ${tokens.INFO_DARK};
  }
`;

export { TaskHeader };
export type { TaskHeaderProps };
